import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { StudentEngagementUtil } from "./student.engagement.util";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { distinctUntilChanged, map, Observable, tap } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { StateService } from "../../services/state.service";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
declare const angular: any;

@TypeManagerDecorator("se-sidebar")
@Component({
    selector: "se-sidebar",
    template: `
        <aside class="sidebar" [class.collapsed]="sidebarCollapsed()">
            <s25-ng-icon-button
                [type]="'doubleArrowLeft'"
                (click)="$event.stopPropagation(); sidebarCollapsed.set(!sidebarCollapsed())"
                class="collapse-btn"
            ></s25-ng-icon-button>
            <nav class="menu">
                @for (view of viewModes; track view) {
                    @let isActive = activeMode() === view.id || activeSubModeParentId() === view.id;
                    <a
                        s25-ng-router-link
                        [stateName]="'studentEngagementAdmin'"
                        [stateParams]="{ mode: view.id }"
                        (click)="activeMode.set(view.id)"
                        class="menu-item"
                        [class.active]="isActive"
                        [attr.data-label]="view.id"
                    >
                        <s25-ng-icon
                            class="menu-icon"
                            [type]="view.iconProp!"
                            [size]="sidebarCollapsed() ? 30 : 20"
                        ></s25-ng-icon>
                        <span class="menu-label">{{ view.name }}</span>
                    </a>
                    @if (!sidebarCollapsed() && view.subModes?.length && isActive) {
                        @for (subMode of view.subModes; track subMode) {
                            <a
                                s25-ng-router-link
                                [stateName]="'studentEngagementAdmin'"
                                [stateParams]="{ mode: subMode.id }"
                                class="menu-item submenu-item"
                                (click)="activeMode.set(subMode.id)"
                                [class.active]="activeMode() === subMode.id"
                                [attr.data-label]="subMode.id"
                            >
                                <span class="menu-label">{{ subMode.name }}</span>
                            </a>
                        }
                    }
                }
            </nav>
        </aside>
    `,
    styles: `
        .sidebar {
            width: 280px;
            height: 100vh;
            background: white;
            border-radius: 16px;
            padding: 24px 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            transition:
                width 0.3s ease,
                padding 0.3s ease;
        }

        .sidebar.collapsed {
            width: 88px;
            padding: 24px 12px;
        }

        .logo-text h1 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #1a1a1a;
        }

        .logo-text p {
            margin: 0;
            font-size: 13px;
            color: #9ca3af;
        }

        .collapse-btn {
            background: none;
            border: none;
            color: #9ca3af;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            border-radius: 4px;
            flex-shrink: 0;
        }

        .menu {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .menu-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            border-radius: 8px;
            color: #6b7280;
            text-decoration: none;
            cursor: pointer;
            transition: all 0.2s;
            position: relative;
            overflow: hidden;
        }

        .sidebar.collapsed .menu-item {
            justify-content: center;
            padding: 12px;
        }

        .menu-item:hover {
            background: #f9fafb;
            color: #374151;
        }

        .menu-item.active {
            background: #e3f2fd;
            color: #1565c0;
            outline-offset: 0;
        }

        .submenu-item {
            margin-left: 1em;
        }

        .menu-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .menu-icon :global(svg) {
            width: 20px;
            height: 20px;
            stroke: currentColor;
        }

        .menu-label {
            font-weight: 500;
            white-space: nowrap;
            opacity: 1;
            transition: opacity 0.3s ease;
        }

        .sidebar.collapsed .menu-label {
            opacity: 0;
            width: 0;
        }

        ::ng-deep .sidebar .collapse-btn svg {
            transition: all 0.3s ease;
        }

        ::ng-deep .sidebar.collapsed .collapse-btn svg {
            transform: rotate(180deg);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconButtonComponent, S25IconComponent, S25RouterLinkDirective],
})
export class SeSidebarComponent {
    protected activeMode = signal<string | null>("dash");
    protected activeSubModeParentId = computed(
        () => this.viewModes.find((mode) => mode.subModes?.some((subMode) => subMode.id === this.activeMode()))?.id,
    );
    protected sidebarCollapsed = signal(false);

    // private seService = inject(StudentEngagementService);

    protected viewModes = StudentEngagementUtil.ViewModes;

    private transitionSuccess$ = new Observable((subscriber) => {
        const $rootScope = angular.element(document.body).injector().get("$rootScope");
        const deregisterFn = $rootScope.$on("$stickyTransitionSuccess", () => subscriber.next());
        return () => deregisterFn();
    });

    protected currentState = toSignal(
        this.transitionSuccess$.pipe(
            map(() => StateService.current()?.component),
            tap((state) => state === "studentEngagement" && this.activeMode.set(null)),
            distinctUntilChanged(),
        ),
    );
}
